
    import _ from 'underscore';
    import {
        computed, defineComponent, onMounted, PropType, Ref, ref, toRefs, unref, watch,
    } from 'vue';
    import DialogWrapperDesktop from '@ve/components/dialogWrapper/desktop/DialogWrapperDesktop.vue';
    import ImagesSlider from '@ve/components/images-slider/common/ImagesSlider.vue';
    import { ImagesSliderPhoto, ImagesSliderVideo } from '@ve/components/images-slider/common/types';
    import imageProxy from '@/js/common/Domain/Service/imageProxy';
    import { useMutations } from 'vuex-composition-helpers';
    import { NAMESPACE as HOTEL_PAGE_GALLERY_STORE } from '@ve/pages/hotel/store/HotelPageGalleryStore';
    import { IHotelPageGalleryStoreMutations } from '@ve/pages/hotel/store/types';

    const AVAILABLE_IMAGES_SIZES = [
        [940, 705, 1240],
    ];

    export default defineComponent({
        name: 'fullscreen-slider',
        components: { ImagesSlider, DialogWrapperDesktop },
        props: {
            images: {
                type: Array as PropType<ImagesSliderPhoto[]>,
                required: false,
                default: [],
            },
            videos: {
                type: Array as PropType<ImagesSliderVideo[]>,
                required: false,
                default: [],
            },
            video: {
                type: Object as PropType<ImagesSliderVideo | null>,
                default: null,
            },
            isVisibleThumbs: {
                type: Boolean,
                default: true,
            },
            isFullScreenSlider: {
                type: Object as PropType<boolean>,
                default: false,
            },
            parentSelectedIndex: {
                type: Object as PropType<number>,
                default: 0,
            },
        },
        setup(props) {
            const popupWrapperRef: Ref<InstanceType<typeof DialogWrapperDesktop> | null> = ref(null);

            const {
                setIsGalleryOpenedInPopup, setIsWatchGalleryUntilTheEnd, setIsClickedOnThumb, addIndexOfWatchPhoto,
            } = useMutations<IHotelPageGalleryStoreMutations>(HOTEL_PAGE_GALLERY_STORE, ['setIsGalleryOpenedInPopup', 'setIsWatchGalleryUntilTheEnd', 'setIsClickedOnThumb', 'addIndexOfWatchPhoto']);

            const {
                images,
                isFullScreenSlider,
                parentSelectedIndex,
            } = toRefs(props);

            const generateSrcSet = (src: string) => _.map(AVAILABLE_IMAGES_SIZES, ([width, height, screenWidth]) => `${
                imageProxy(src, {
                    width,
                    height,
                })} ${screenWidth ? `${screenWidth}w` : ''}`).join(', ');

            const thumbs = computed(() => unref(images).map((image) => ({
                alt: image.alt,
                src: imageProxy(image.src, {
                    width: 92,
                    height: 69,
                }),
            })));

            const sizedImages = computed(() => unref(images).map((image) => ({
                alt: image.alt,
                src: imageProxy(image.src, {
                    width: 940,
                    height: 705,
                }),
                srcset: generateSrcSet(image.src),
            })));

            const selectedIndex = ref(0);

            const key = ref(0);

            const open = () => {
                unref(popupWrapperRef)?.open();
                selectedIndex.value = parentSelectedIndex.value;
                isFullScreenSlider.value = false;
                key.value++;
            };

            const isSliderOpened = computed(() => unref(isFullScreenSlider));
            watch(isSliderOpened, (value) => {
                if (value) open();
            });

            onMounted(() => {
                setIsGalleryOpenedInPopup(true);
                open();
            });

            const slideChangeHandler = (index: number) => {
                addIndexOfWatchPhoto(index);
                selectedIndex.value = index;
            };

            return {
                open,
                slideChangeHandler,
                setIsWatchGalleryUntilTheEnd,
                setIsClickedOnThumb,
                key,
                popupWrapperRef,
                thumbs,
                sizedImages,
                selectedIndex,
            };
        },
    });
